import { useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../../api/client";
import type {
  InspectionDecision,
  InspectionStatus,
  Me,
  ReceivingItem,
} from "../../api/types";
import { S } from "../../styles";
import { hasRole } from "../../auth/roleHelpers";
import {
  LoadingState,
  ErrorState,
  EmptyState,
} from "../../components/QueryStates";
import { ReceivedCount } from "../../components/OrderLine";
import { DiscrepancyList } from "./DiscrepancyList";
import { CertificatePanel, CommitmentSummary } from "./CertificatePanel";
import { ShipmentHistory } from "./ShipmentHistory";

/**
 * Receiving inspection — the goods-in queue and its verdicts.
 *
 * The authority split here mirrors the server, and it is not symmetric on
 * purpose. Anyone in the ops chain can quarantine: that is the safe direction,
 * and someone holding a part they are unsure about must never have to find a
 * manager before setting it aside. Accepting or waiving narrows to a manager,
 * because releasing a part whose paperwork does not match what was ordered is
 * an airworthiness decision, and the person who made it is the one an auditor
 * asks about afterwards.
 */

const STATUS_TONE: Record<
  InspectionStatus,
  { bg: string; fg: string; label: string }
> = {
  pending: { bg: "#f6f6f6", fg: "#666", label: "Awaiting inspection" },
  discrepancy: { bg: "#fff5f5", fg: "#a61e1e", label: "⚠ Discrepancy" },
  passed: { bg: "#f4fbf6", fg: "#1f7a3d", label: "✓ Passed" },
  quarantined: { bg: "#fff5f5", fg: "#a61e1e", label: "⛔ Quarantined" },
  waived: { bg: "#fffaf0", fg: "#8a5a1c", label: "Waived" },
};

/**
 * A human release rendered as a machine pass, which it is not.
 *
 * `accept` writes `passed` — the same value reconciliation writes when it finds
 * nothing wrong — so a shipment a manager cleared over two blocking findings
 * showed the identical green "✓ Passed" as a pack whose paperwork was clean.
 * The header then read "✓ Passed · 2 blocking", which is self-contradictory, and
 * the natural reading is that the checks are broken.
 *
 * `by` is the actor, so the badge can say who owns the verdict. Green is
 * reserved for verdicts the reconciliation actually produced.
 */
export function InspectionBadge({
  status,
  by,
}: {
  status: InspectionStatus;
  by?: string | null;
}) {
  const tone =
    status === "passed" && by
      ? { bg: "#fffaf0", fg: "#8a5a1c", label: "✓ Accepted" }
      : (STATUS_TONE[status] ?? STATUS_TONE.pending);
  return (
    <span
      style={{
        background: tone.bg,
        color: tone.fg,
        borderRadius: 10,
        padding: "1px 8px",
        fontSize: 11,
        fontWeight: 600,
      }}
    >
      {tone.label}
    </span>
  );
}

/**
 * Why a shipment is back in the queue after having been decided.
 *
 * A shipment reopened by a corrected shipping notice has surrendered its
 * verdict — the status is the machine's again and the actor is null — so
 * nothing else on the card distinguishes it from one nobody has ever ruled on.
 * An inspector who cleared this part yesterday and finds it in their queue today
 * is owed the reason on the row, not in the audit tab.
 */
export function ReopenedNotice({
  at,
  reason,
}: {
  at?: string | null;
  reason?: string | null;
}) {
  if (!at) return null;
  return (
    <div
      style={{
        marginTop: 6,
        fontSize: 12,
        color: "#8a1c1c",
        background: "#fff5f5",
        border: "1px solid #ffcfcf",
        borderRadius: 4,
        padding: "4px 8px",
      }}
    >
      ⟳ Re-opened {new Date(at).toLocaleString()} —{" "}
      {reason ?? "the shipped lines changed after it was decided"} Any earlier
      decision no longer stands; the checks were re-run against the corrected
      pack.
    </div>
  );
}

// What the person did, per the status their decision wrote. `passed` reads
// "Accepted" because that is the button they pressed — the machine writes the
// same value and this line only ever renders when there was an actor.
const VERDICT_VERB: Record<string, string> = {
  passed: "Accepted",
  waived: "Waived",
  quarantined: "Quarantined",
};

export function ReceivingPanel({ me }: { me: Me | null }) {
  const queryClient = useQueryClient();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [includeClosed, setIncludeClosed] = useState(false);
  const [actionErr, setActionErr] = useState<string | null>(null);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const fileInput = useRef<HTMLInputElement | null>(null);

  const canRelease = hasRole(me, "manager");
  // Same gate as the PO and RFQ screens — the audit API answers 403 otherwise,
  // and a button that always errors is worse than no button.
  const canViewAudit = hasRole(me, "finance", "manager");

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["receiving-queue", includeClosed],
    queryFn: () => api.listReceivingQueue(includeClosed),
    refetchInterval: 15_000,
  });
  const items = data ?? [];

  const { data: documents, isLoading: docsLoading } = useQuery({
    queryKey: ["receiving-documents", expandedId],
    queryFn: () => api.getShipmentDocuments(expandedId!),
    enabled: !!expandedId,
    staleTime: 10_000,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["receiving-queue"] });
    void queryClient.invalidateQueries({ queryKey: ["receiving-documents"] });
  };

  const verdictMutation = useMutation({
    mutationFn: ({
      shipmentId,
      decision,
      note,
    }: {
      shipmentId: string;
      decision: InspectionDecision;
      note?: string;
    }) => api.recordInspectionVerdict(shipmentId, decision, note),
    onSuccess: () => {
      setActionErr(null);
      invalidate();
    },
    onError: (e) => setActionErr(e.message),
  });

  const reconcileMutation = useMutation({
    mutationFn: (shipmentId: string) => api.reconcileShipment(shipmentId),
    onSuccess: () => {
      setActionErr(null);
      invalidate();
    },
    onError: (e) => setActionErr(e.message),
  });

  const exclusionMutation = useMutation({
    mutationFn: ({
      documentId,
      excluded,
      reason,
    }: {
      documentId: string;
      excluded: boolean;
      reason?: string;
    }) => api.setTraceDocumentExclusion(documentId, excluded, reason),
    onSuccess: () => {
      setActionErr(null);
      // Both: the server re-ran the checks, so the shipment's findings and
      // blocking count changed as well as the document.
      invalidate();
    },
    onError: (e) => setActionErr(e.message),
  });

  const uploadMutation = useMutation({
    mutationFn: ({
      poId,
      file,
      shipmentId,
    }: {
      poId: string;
      file: File;
      shipmentId: string;
    }) => api.uploadPODocument(poId, file, shipmentId),
    onSuccess: () => {
      setActionErr(null);
      invalidate();
    },
    onError: (e) => setActionErr(e.message),
  });

  if (isLoading) return <LoadingState label="Loading receiving queue…" />;
  if (error) return <ErrorState error={error} />;

  const blocked = items.filter((i) => i.inspection_status === "discrepancy");

  const renderItem = (item: ReceivingItem) => {
    const isExpanded = expandedId === item.shipment_id;
    const busy =
      verdictMutation.isPending ||
      reconcileMutation.isPending ||
      uploadMutation.isPending ||
      exclusionMutation.isPending;

    return (
      <div key={item.shipment_id} style={S.card}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <div>
            <span style={{ fontWeight: 700, fontSize: 15 }}>
              {item.po_number}
            </span>
            <span style={{ marginLeft: 10 }}>
              <InspectionBadge
                status={item.inspection_status}
                by={item.inspected_by_user_id}
              />
            </span>
            {item.blocking_count > 0 && (
              <span style={{ ...S.muted, marginLeft: 8, fontSize: 11 }}>
                {item.blocking_count} blocking
              </span>
            )}
          </div>
          <button
            style={S.btnSm}
            onClick={() => setExpandedId(isExpanded ? null : item.shipment_id)}
          >
            {isExpanded ? "▲ Collapse" : "▼ Inspect"}
          </button>
        </div>

        <div style={{ marginTop: 6, fontSize: 13 }}>
          <strong>{item.part_number ?? "—"}</strong>
          {item.condition_code && (
            <span style={{ ...S.muted, marginLeft: 6 }}>
              ({item.condition_code})
            </span>
          )}
          {item.quantity != null && (
            <span style={{ marginLeft: 8 }}>{item.quantity} ordered</span>
          )}
          {item.quantity_received != null && (
            <span style={{ marginLeft: 8 }}>
              ·{" "}
              <ReceivedCount
                received={item.quantity_received}
                ordered={item.quantity}
              />
            </span>
          )}
          {item.supplier_name && (
            <span style={{ ...S.muted, marginLeft: 8 }}>
              from {item.supplier_name}
            </span>
          )}
        </div>

        <div style={{ ...S.muted, marginTop: 4, fontSize: 12 }}>
          {item.document_count === 0
            ? "No certificate received"
            : `${item.document_count} document(s)`}
          {item.excluded_document_count > 0 &&
            ` · ${item.excluded_document_count} set aside`}
          {item.received_at &&
            ` · received ${new Date(item.received_at).toLocaleString()}`}
        </div>

        <ReopenedNotice
          at={item.inspection_reopened_at}
          reason={item.inspection_reopened_reason}
        />

        {/*
          Said in words, not left to be inferred from a badge. The findings stay
          on screen after a human verdict — deliberately, an auditor needs to see
          what the decision was taken over — so without this line the card shows
          blocking findings beside a cleared status and explains neither.
        */}
        {item.inspected_by_user_id && (
          <div
            style={{
              marginTop: 6,
              fontSize: 12,
              color: "#8a5a1c",
              background: "#fffaf0",
              border: "1px solid #ffe3bf",
              borderRadius: 4,
              padding: "4px 8px",
            }}
          >
            {VERDICT_VERB[item.inspection_status] ?? "Ruled on"} by{" "}
            {item.inspected_by_email ?? "a user"}
            {item.inspected_at &&
              ` on ${new Date(item.inspected_at).toLocaleString()}`}
            {item.blocking_count > 0 &&
              ` — over ${item.blocking_count} blocking finding(s), which stay on the record`}
          </div>
        )}

        {isExpanded && (
          <div
            style={{
              marginTop: 12,
              borderTop: "1px solid #f0f0f0",
              paddingTop: 12,
            }}
          >
            <h3 style={{ ...S.h3, fontSize: 14, marginBottom: 8 }}>
              What this order required
            </h3>
            <CommitmentSummary commitment={item.trace_commitment} />

            <h3 style={{ ...S.h3, fontSize: 14, margin: "12px 0 8px" }}>
              Findings
            </h3>
            <DiscrepancyList
              findings={item.inspection_findings}
              documents={documents}
            />

            <h3 style={{ ...S.h3, fontSize: 14, margin: "12px 0 8px" }}>
              Documents{docsLoading ? " (loading…)" : ""}
            </h3>
            {!docsLoading && (documents ?? []).length === 0 ? (
              <p style={{ ...S.muted, fontSize: 12 }}>
                Nothing received yet. Attach the certificate when it arrives —
                the checks re-run automatically.
              </p>
            ) : (
              (documents ?? []).map((doc) => (
                <CertificatePanel
                  key={doc.id}
                  doc={doc}
                  findings={item.inspection_findings}
                  canExclude={canRelease}
                  busy={busy}
                  // mutateAsync, not mutate: the certificate's own form keeps
                  // the reason on screen until the write lands, so it needs the
                  // outcome rather than fire-and-forget.
                  onExclusionChange={async (documentId, excluded, reason) => {
                    await exclusionMutation.mutateAsync({
                      documentId,
                      excluded,
                      reason,
                    });
                  }}
                />
              ))
            )}

            <div style={{ marginTop: 10 }}>
              <input
                ref={fileInput}
                type="file"
                accept="application/pdf,image/png,image/jpeg"
                style={{ display: "none" }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  uploadMutation.mutate({
                    poId: item.purchase_order_id,
                    file,
                    shipmentId: item.shipment_id,
                  });
                  e.target.value = "";
                }}
              />
              <button
                style={S.btnSm}
                onClick={() => fileInput.current?.click()}
                disabled={busy}
              >
                📎 Attach certificate
              </button>
              <button
                style={{ ...S.btnSm, marginLeft: 8 }}
                onClick={() => reconcileMutation.mutate(item.shipment_id)}
                disabled={busy}
              >
                ↺ Re-run checks
              </button>
            </div>

            <div style={{ marginTop: 12 }}>
              <input
                style={{ ...S.input, marginBottom: 8 }}
                placeholder="Inspection notes (optional)…"
                value={notes[item.shipment_id] ?? ""}
                onChange={(e) =>
                  setNotes((prev) => ({
                    ...prev,
                    [item.shipment_id]: e.target.value,
                  }))
                }
              />
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button
                  style={{
                    ...S.btn,
                    background: "#c92a2a",
                    padding: "7px 16px",
                  }}
                  onClick={() =>
                    verdictMutation.mutate({
                      shipmentId: item.shipment_id,
                      decision: "quarantine",
                      note: notes[item.shipment_id],
                    })
                  }
                  disabled={busy}
                >
                  ⛔ Quarantine
                </button>
                {canRelease ? (
                  <>
                    <button
                      style={{
                        ...S.btn,
                        background: "#2f9e44",
                        padding: "7px 16px",
                      }}
                      onClick={() =>
                        verdictMutation.mutate({
                          shipmentId: item.shipment_id,
                          decision: "accept",
                          note: notes[item.shipment_id],
                        })
                      }
                      disabled={busy}
                    >
                      ✓ Accept
                    </button>
                    <button
                      style={{
                        ...S.btn,
                        background: "#e67700",
                        padding: "7px 16px",
                      }}
                      onClick={() =>
                        verdictMutation.mutate({
                          shipmentId: item.shipment_id,
                          decision: "waive",
                          note: notes[item.shipment_id],
                        })
                      }
                      disabled={busy}
                    >
                      Waive
                    </button>
                  </>
                ) : (
                  // A buyer sees exactly what is wrong and can hold the part;
                  // they cannot release it. Same rule the server enforces.
                  <span
                    style={{ ...S.muted, fontSize: 12, alignSelf: "center" }}
                  >
                    A manager must accept or waive this shipment.
                  </span>
                )}
              </div>
            </div>

            {canViewAudit && <ShipmentHistory shipmentId={item.shipment_id} />}
          </div>
        )}

        {actionErr && <p style={{ ...S.err, marginTop: 8 }}>{actionErr}</p>}
      </div>
    );
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <h2 style={{ ...S.h2, margin: 0 }}>
          🔍 Receiving Inspection{" "}
          {blocked.length > 0 && (
            <span
              style={{
                fontSize: 13,
                background: "#c92a2a",
                color: "#fff",
                borderRadius: 10,
                padding: "1px 7px",
              }}
            >
              {blocked.length} with discrepancies
            </span>
          )}
        </h2>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <label style={{ ...S.muted, fontSize: 12 }}>
            <input
              type="checkbox"
              checked={includeClosed}
              onChange={(e) => setIncludeClosed(e.target.checked)}
              style={{ marginRight: 4 }}
            />
            Include closed
          </label>
          <button style={S.btnSm} onClick={() => void refetch()}>
            ↺ Refresh
          </button>
        </div>
      </div>

      {items.length === 0 ? (
        <EmptyState message="Nothing awaiting inspection." />
      ) : (
        items.map(renderItem)
      )}
    </div>
  );
}
